import React, { useEffect, useState, useImperativeHandle, forwardRef, useCallback } from "react";
import { useQueryClient } from 'react-query';
import ProgramDetailsModal from "./modals/ProgramDetailsModal";
import ProgramModal from "./modals/ProgramModal";
import Table from "../common/Table";
import { communityServicesAPI } from "../../services/api";
import ConfirmationModal from "../common/ConfirmationModal";
import toast from "react-hot-toast";
import { EyeIcon, PencilIcon, TrashIcon, CalendarIcon, MapPinIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const ProgramsTable = forwardRef(({
  filters = { status: "", start_date: "", end_date: "", search: "" },
  onRefreshNeeded,
  showFilter = false,
  toggleFilter = () => { },
  onFilterChange = () => { }
}, ref) => {
  const queryClient = useQueryClient();
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [meta, setMeta] = useState({ total: 0, current_page: 1, last_page: 1, per_page: 10 });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEditProgramId, setSelectedEditProgramId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [filterOpen, setFilterOpen] = useState(showFilter);


  const statusColors = {
    Planned: "bg-yellow-100 text-yellow-700",
    Ongoing: "bg-blue-100 text-blue-700",
    Completed: "bg-green-100 text-green-700",
    Cancelled: "bg-red-100 text-red-700",
  };

  // Fetch programs
  useEffect(() => {
    setCurrentPage(1);
    fetchPrograms(1, rowsPerPage, filters);
  }, [filters]);

  const fetchPrograms = useCallback(async (page = 1, perPage = rowsPerPage, appliedFilters) => {
  setLoading(true);
  try {


    const response = await communityServicesAPI.getCommunityServices({
      page,
      per_page: perPage,
      search: appliedFilters.search || "",
      status: appliedFilters.status || undefined,
      start_date: appliedFilters.start_date || undefined,
      end_date: appliedFilters.end_date || undefined,
    });

    const resData = response.data?.data || {};

    setFilteredPrograms(resData.data || []);
    setMeta({
      total: resData.meta?.total || 0,
      current_page: resData.meta?.current_page || page,
      last_page: resData.meta?.last_page || 1,
      per_page: perPage
    });
  } catch (err) {
    console.error('Failed to fetch programs:', err);
  } finally {
    setLoading(false);
  }
}, [rowsPerPage]);


  useEffect(() => {
    setCurrentPage(1);
    fetchPrograms(1, rowsPerPage, filters);
  }, [filters]);

  useImperativeHandle(ref, () => ({
    refresh: () => fetchPrograms(currentPage, rowsPerPage, filters),
    refreshWithForce: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'community-services'
      }).then(() => fetchPrograms(currentPage, rowsPerPage, filters));
    }
  }));

  // Search handlers
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);

    const timeout = setTimeout(() => {
      if (onRefreshNeeded) onRefreshNeeded({ ...filters, search: value });
    }, 300);
    setSearchTimeout(timeout);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (searchTimeout) clearTimeout(searchTimeout);
      if (onRefreshNeeded) onRefreshNeeded({ ...filters, search: localSearch });
    }
  };

  const handleSearchBlur = () => {
    if (searchTimeout) clearTimeout(searchTimeout);
    if (localSearch !== filters.search && onRefreshNeeded) {
      onRefreshNeeded({ ...filters, search: localSearch });
    }
  };

  useEffect(() => () => { if (searchTimeout) clearTimeout(searchTimeout); }, [searchTimeout]);

  // Formatters
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "-";
    const date = new Date(dateTimeString);
    return `${date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" })}, ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`;
  };

  const handleRowsPerPageChange = (value) => {
    const perPage = parseInt(value, 10);
    setRowsPerPage(perPage);
    setCurrentPage(1);
    fetchPrograms(1, perPage, filters);
  };


  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPrograms(page, rowsPerPage, filters);
  };

  // Program actions
  const handleView = (programId) => { setSelectedProgramId(programId); setIsModalOpen(true); };
  const handleEdit = (programId) => { setSelectedEditProgramId(programId); setIsEditModalOpen(true); };
  const handleCloseEditModal = () => { setIsEditModalOpen(false); setSelectedEditProgramId(null); };
  const handleSaveEditProgram = () => {
    queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'community-services' })
      .then(() => fetchPrograms(currentPage, rowsPerPage, filters));
    if (onRefreshNeeded) onRefreshNeeded();
  };
  const handleDelete = (id) => { setDeleteTargetId(id); setIsConfirmOpen(true); };
  const confirmDelete = async (id) => {
    const toastId = toast.loading("Deleting program...");
    try {
      await communityServicesAPI.deleteProgram(id);
      toast.success("Program deleted successfully", { id: toastId });
      queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'community-services' })
        .then(() => fetchPrograms(currentPage, rowsPerPage, filters));
      if (onRefreshNeeded) onRefreshNeeded();
    } catch (error) {
      toast.error("Failed to delete program. Please try again.", { id: toastId });
    } finally {
      setIsConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleCloseModal = () => { setIsModalOpen(false); setSelectedProgramId(null); };

  // Columns
  const columns = [
    {
      key: "program_info",
      label: "Program Info",
      render: (_, program) => (
        <div className="flex items-start">
          <div className="flex-shrink-0 h-12 w-12 mr-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-indigo-100">
              <CalendarIcon className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">{program.name}</div>
            <div className="flex items-center text-xs text-gray-500 mt-0.5">
              <CalendarIcon className="h-3 w-3 text-gray-400 mr-1" />
              <span className="truncate" title={formatDateTime(program.start_date || program.created_at)}>
                {formatDateTime(program.start_date || program.created_at)}
              </span>
            </div>
            <div className="flex items-center text-xs text-gray-500 mt-0.5">
              <MapPinIcon className="h-3 w-3 text-gray-400 mr-1" />
              <span className="truncate max-w-xs">{program.location || "No location specified"}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "volunteers",
      label: "Volunteers",
      render: (_, program) => (
        <div className="flex items-center -space-x-2">
          {program.volunteers?.length > 0 ? (
            <>
              {program.volunteers.slice(0, 3).map((v, i) => {
                const volunteerName = v.volunteer_name || 
                                      v?.resident?.full_name || 
                                      v?.full_name || 
                                      v?.name || 
                                      "Volunteer";
                
                const initials = volunteerName
                  .split(" ")
                  .filter(Boolean)
                  .map(n => n[0])
                  .join("")
                  .toUpperCase() || "?";
                
                return (
                  <div 
                    key={i} 
                    className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold border-2 border-white" 
                    title={volunteerName}
                  >
                    {initials}
                  </div>
                );
              })}
              {program.volunteers.length > 3 && (
                <div className="w-8 h-8 rounded-full bg-gray-300 text-gray-700 flex items-center justify-center text-xs font-semibold border-2 border-white"
                  title={`${program.volunteers.length - 3} more`}>
                  +{program.volunteers.length - 3}
                </div>
              )}
            </>
          ) : <span className="text-sm text-gray-400">No Volunteers</span>}
        </div>
      ),
    },
    { key: "total_volunteers", label: "Participants", render: (_, p) => <div className="text-center text-sm font-medium">{p.total_volunteers || 0}</div> },
    {
      key: "status", label: "Status", render: (_, p) => {
        const status = p.status ? p.status.charAt(0).toUpperCase() + p.status.slice(1).toLowerCase() : "-";
        const colorClass = statusColors[status] || "bg-gray-100 text-gray-700";
        return <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${colorClass}`}>{status}</span>;
      }
    },
    {
      key: "actions", label: "Actions", render: (_, p) => (
        <div className="flex items-center justify-end space-x-1">
          <button onClick={() => handleView(p.program_id)} className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50"><EyeIcon className="h-4 w-4" /></button>
          <button onClick={() => handleEdit(p.program_id)} className="text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50"><PencilIcon className="h-4 w-4" /></button>
          <button onClick={() => handleDelete(p.program_id)} className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"><TrashIcon className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="mt-4">
      <div className="bg-white shadow rounded-t-lg overflow-hidden border border-gray-200">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b">
          <h2 className="font-semibold text-gray-800">
            Scheduled Programs
          </h2>
          <div className="flex items-center gap-2">
           <div className="relative w-64">
              <input
                type="text"
                value={localSearch}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onBlur={handleSearchBlur}
                placeholder="Search community programs..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button onClick={() => { toggleFilter(); setFilterOpen(!filterOpen); }} className={`flex items-center gap-1 px-5 py-2 border rounded-lg text-sm transition ${filterOpen ? "border-blue-500 text-blue-600" : "border-gray-400 text-gray-600 hover:bg-gray-50"}`}>
              <FunnelIcon className={`h-5 w-5 ${filterOpen ? "text-blue-600" : "text-gray-500"}`} /> Filter
            </button>
          </div>
        </div>

        {/* Filter Section */}
        {filterOpen && (
          <div className="px-4 py-3 border-b bg-gray-50 grid grid-cols-1 sm:grid-cols-3 gap-4 animate-in fade-in-50 duration-300">

            {/* Status */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select
                value={filters.status || "All Status"}
                onChange={(e) => {
                  const value = e.target.value;
                  onFilterChange({ ...filters, status: value === "All Status" ? "" : value });
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              >
                <option>All Status</option>
                <option>Planned</option>
                <option>Ongoing</option>
                <option>Completed</option>
                <option>Cancelled</option>
              </select>
            </div>

            {/* Start Date */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date || ""}
                onChange={(e) => onFilterChange({ ...filters, start_date: e.target.value })}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
            </div>

            {/* End Date */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date || ""}
                onChange={(e) => onFilterChange({ ...filters, end_date: e.target.value })}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
              />
            </div>

          </div>
        )}


        {/* Table */}
        <div className="p-0">
          <Table columns={columns} data={filteredPrograms} getRowKey={(p) => p.program_id} pagination={{ currentPage, lastPage: meta.last_page, perPage: rowsPerPage, total: meta.total }} onPageChange={handlePageChange} paginationProps={{ onPerPageChange: handleRowsPerPageChange, isLoading: loading, showSizeSelector: true }} />
        </div>
      </div>

      {/* Modals */}
      <ProgramDetailsModal isOpen={isModalOpen} onClose={handleCloseModal} programId={selectedProgramId} onEdit={(id) => { setIsModalOpen(false); handleEdit(id || selectedProgramId); }} onRefresh={handleSaveEditProgram} />
      <ProgramModal isOpen={isEditModalOpen} onClose={handleCloseEditModal} mode="edit" programId={selectedEditProgramId} onSuccess={handleSaveEditProgram} />
      <ConfirmationModal isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} onConfirm={() => deleteTargetId && confirmDelete(deleteTargetId)} title="Delete Program" message="Are you sure you want to delete this program? This action cannot be undone." confirmText="Delete" cancelText="Cancel" confirmButtonClass="bg-red-600 hover:bg-red-700 text-white" />
    </div>
  );
});

export default ProgramsTable;
